import React, { useState } from 'react';
import { Send, Loader2, CheckCircle2, MessageSquareHeart, X } from 'lucide-react';
import { RexVideoRecorder } from '../components/rex/RexVideoRecorder';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 4 * 1024 * 1024;

const FIELDS = [
  { id: 'appreciated', label: "Ce que j'ai apprécié", ph: 'Les mécaniques, les moments forts, ce qui vous a plu…' },
  { id: 'regrets', label: 'Ce que je regrette', ph: 'Ce qui vous a manqué, frustré ou déçu…' },
  { id: 'improvements', label: "Les axes d'amélioration", ph: 'Vos idées pour rendre la simulation encore meilleure…' },
  { id: 'overall', label: 'Mon ressenti global', ph: 'En quelques mots, votre expérience dans son ensemble…' },
];

/** Formulaire REX (retour d'expérience) apprenant — 4 textes + vidéo facultative.
 *  Utilisé en modale (invitation en jeu) et en plein écran (licence expirée). */
export default function RexPage({ licenseKey, holderName, onClose, onSubmitted }) {
  const [texts, setTexts] = useState({ appreciated: '', regrets: '', improvements: '', overall: '' });
  const [videoBlob, setVideoBlob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const hasContent = Object.values(texts).some((t) => t.trim()) || videoBlob;

  const uploadVideo = async () => {
    const initRes = await fetch(`${API}/api/feedback/rex/upload/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ license_key: licenseKey }),
    });
    if (!initRes.ok) throw new Error('init upload');
    const { upload_id } = await initRes.json();
    const total = Math.ceil(videoBlob.size / CHUNK_SIZE);
    for (let i = 0; i < total; i += 1) {
      const form = new FormData();
      form.append('upload_id', upload_id);
      form.append('index', String(i));
      form.append('file', videoBlob.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), `part_${i}.webm`);
      const res = await fetch(`${API}/api/feedback/rex/upload/chunk`, { method: 'POST', body: form });
      if (!res.ok) throw new Error(`chunk ${i}`);
      setProgress(Math.round(((i + 1) / total) * 90));
    }
    return upload_id;
  };

  const submit = async () => {
    if (!hasContent || submitting) return;
    setSubmitting(true);
    setError('');
    setProgress(videoBlob ? 5 : 50);
    try {
      const uploadId = videoBlob ? await uploadVideo() : null;
      setProgress(95);
      const res = await fetch(`${API}/api/feedback/rex/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: licenseKey, ...texts, upload_id: uploadId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setProgress(100);
      setDone(true);
      if (typeof onSubmitted === 'function') onSubmitted();
    } catch (e) {
      setError(String(e.message || e) === 'init upload' || String(e.message || e).startsWith('chunk')
        ? "L'envoi de la vidéo a échoué. Réessayez, ou envoyez votre retour sans vidéo."
        : String(e.message || e));
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-14 px-6" data-testid="rex-success">
        <CheckCircle2 className="w-14 h-14 text-money mx-auto mb-4" />
        <h2 className="font-display text-2xl font-bold text-foreground mb-2">Merci pour votre retour !</h2>
        <p className="text-sm text-slate-400">
          Votre témoignage a bien été transmis à l'équipe HST. Il nous aide directement à améliorer la simulation pour les prochains apprenants.
        </p>
        {onClose && (
          <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-accent text-white rounded-full text-sm font-bold hover:bg-accent-hover" data-testid="rex-close-after-success">
            Fermer
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="rex-form">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-xl font-bold text-foreground flex items-center gap-2">
            <MessageSquareHeart className="w-5 h-5 text-accent" /> Votre retour d'expérience
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            {holderName ? `${holderName}, votre` : 'Votre'} avis compte : quelques minutes pour nous aider à faire progresser HST.
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-foreground rounded" data-testid="rex-close">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        <RexVideoRecorder onVideo={setVideoBlob} />

        {FIELDS.map((f) => (
          <div key={f.id}>
            <label htmlFor={`rex-${f.id}`} className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
              {f.label}
            </label>
            <textarea
              id={`rex-${f.id}`}
              value={texts[f.id]}
              onChange={(e) => setTexts((prev) => ({ ...prev, [f.id]: e.target.value }))}
              placeholder={f.ph}
              maxLength={2000}
              className="w-full min-h-[84px] bg-background border border-ink-600 rounded-lg p-3 text-sm text-foreground placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent resize-vertical"
              data-testid={`rex-input-${f.id}`}
            />
          </div>
        ))}

        {error && (
          <p className="text-sm text-danger bg-danger/10 border border-danger/40 rounded-lg px-4 py-2.5" data-testid="rex-error">{error}</p>
        )}

        {submitting && videoBlob && (
          <div className="w-full bg-ink-700/40 rounded-full h-2 overflow-hidden" data-testid="rex-progress">
            <div className="h-full bg-accent transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}

        <button
          onClick={submit}
          disabled={!hasContent || submitting}
          className="w-full flex items-center justify-center gap-2 bg-accent text-white py-3 rounded-full text-sm font-bold hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="rex-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? (videoBlob ? `Envoi de la vidéo… ${progress}%` : 'Envoi…') : 'Envoyer mon retour'}
        </button>
        <p className="text-center text-[10px] text-slate-600">Texte seul, vidéo seule, ou les deux — au moins un des deux est requis.</p>
      </div>
    </div>
  );
}
